import {
  createPatch,
  formatHumanName as medplumFormatHumanName,
  getExtension as medplumGetExtension,
  getExtensionValue as medplumGetExtensionValue,
  OperationOutcomeError,
} from "@medplum/core";
import {
  Extension,
  ExtensionValue,
  HumanName,
  Resource,
} from "@medplum/fhirtypes";

import { isArray } from "@/lib/utils/typeof";

/**
 * Re-exported so every Medplum util comes from `@/lib/utils`.
 */
export { createPatch };

export const formatHumanName = (
  input: { name?: HumanName[] } | HumanName[] | HumanName | null | undefined
): string => {
  if (!input) {
    return "";
  }

  if (isArray(input)) {
    return medplumFormatHumanName(input[0]);
  }

  if ("name" in input) {
    return medplumFormatHumanName((input as { name?: HumanName[] }).name?.[0]);
  }

  return medplumFormatHumanName(input as HumanName);
};

export const getExtension = (
  resource: Resource | Extension | null | undefined,
  ...urls: string[]
): Extension | undefined => medplumGetExtension(resource, ...urls);

export const getExtensionValue = <T extends ExtensionValue>(
  resource: Resource | Extension | null | undefined,
  ...urls: string[]
): T | undefined =>
  medplumGetExtensionValue(resource, ...urls) as T | undefined;

/**
 * The repeat-aware sibling of `getExtension`, which resolves each level with
 * `.find()` and so only ever returns the first match. One url, not variadic:
 * both call sites read a repeat directly off a container they already hold
 * (`Basic` for `follow-up`, a `follow-up` `Extension` for `send-window`), and
 * a variadic version would have to decide which level repeats.
 *
 * Typed structurally rather than as `Resource | Extension`: `Resource` is a
 * union whose members do not all carry `extension`, so that signature would
 * need a cast here instead of rejecting `Bundle` at the call site.
 */
export const getExtensions = (
  resource: { extension?: Extension[] } | null | undefined,
  url: string
): Extension[] =>
  (resource?.extension ?? []).filter(
    ({ url: extensionUrl }) => extensionUrl === url
  );

/**
 * Distinguishes "someone else wrote to this resource" from any other save
 * failure, so the caller can show conflict-specific copy. The server reports
 * the `test` op failing as a plain 400, not 412 — the thrown
 * `OperationOutcomeError`'s `message` is the FHIR server's `TestError` text
 * verbatim (`"Test failed: <actual> != <expected>"`), which is the only thing
 * that tells this case apart from any other `badRequest`.
 */
export const isResourceVersionConflict = (error: unknown): boolean =>
  error instanceof OperationOutcomeError &&
  error.message.includes("Test failed");
